import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">{t('common.backToList')}</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: t('common.breadcrumbHome'), href: '/' },
    { label: category?.name || '', href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    inLanguage: 'zh-CN',
    articleSection: category?.name,
    wordCount: article.content.replace(/<[^>]*>/g, '').length,
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - ${t('common.siteTitle')}`,
          description: article.excerpt,
          inLanguage: 'zh-CN',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.label,
              item: item.href ? `https://family-relationships-guide.com${item.href}` : undefined,
            })),
          },
        }}
      />
      <Navbar />
      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-block text-xs font-medium text-primary-600 bg-primary-100/70 px-3 py-1.5 rounded-full mb-4 hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 border-b border-background-200 pb-6">
                <span>{t('article.authorPrefix')}：{article.author}</span>
                <span>{t('article.publishedDate')}：{article.date}</span>
                <span>{t('article.readTime')}：{article.readTime}</span>
              </div>
            </header>

            <div className="relative w-full h-64 md:h-80 mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - ${t('common.siteTitle')}`}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                {t('common.relatedArticles')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => {
                  const cat = categories.find((c) => c.slug === ra.category);
                  return (
                    <ArticleCard
                      key={ra.id}
                      id={ra.id}
                      title={ra.title}
                      excerpt={ra.excerpt}
                      category={ra.category}
                      author={ra.author}
                      date={ra.date}
                      readTime={ra.readTime}
                      image={ra.image}
                      categoryName={cat?.name}
                      categorySlug={cat?.slug}
                    />
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}